import React from 'react';
import {
    FaExclamationTriangle, FaShieldAlt, FaMoneyBillWave,
    FaCarCrash, FaGlobe, FaCalendarAlt, FaCog, FaGasPump
} from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import './styling/FullCheckv2.css';

const data = {
    writeOffRecordCount: 0,
    lookupStatusMessage: null,
    vrm: "E12FRE",
    vicTestDate: null,
    dateFirstRegistered: "31/07/2019",
    stolenInfoSource: null,
    model: "FIESTA ST-2 TURBO",
    previousColour: null,
    stolenStatus: null,
    previousKeeperCount: 1,
    writeOffDate: null,
    importDate: null,
    stolenPoliceForce: null,
    fuelType: "PETROL",
    lookupStatusCode: null,
    certificateOfDestructionIssued: null,
    latestColourChangeDate: null,
    data: null,
    writeOffCategory: null,
    importedFromOutsideEu: false,
    make: "FORD",
    latestV5cIssuedDate: "22/11/2022",
    mileageRecordList: [
        { dateOfInformation: "13/11/2025", sourceOfInformation: "MOT", mileage: 71064 },
        { dateOfInformation: "13/11/2025", sourceOfInformation: "MOT", mileage: 71064 },
        { dateOfInformation: "04/11/2024", sourceOfInformation: "MOT", mileage: 56831 },
        { dateOfInformation: "26/10/2024", sourceOfInformation: "MOT", mileage: 55991 },
        { dateOfInformation: "06/10/2023", sourceOfInformation: "MOT", mileage: 38966 },
        { dateOfInformation: "05/11/2022", sourceOfInformation: "MOT", mileage: 28263 },
        { dateOfInformation: "28/07/2022", sourceOfInformation: "MOT", mileage: 25907 },
        { dateOfInformation: "28/07/2022", sourceOfInformation: "MOT", mileage: 25907 }
    ],
    mileageAnomalyDetected: false,
    vinLast5: "21774",
    dateFirstRegisteredUk: "31/07/2019",
    writeOffRecordList: null,
    stolenDate: null,
    latestKeeperChangeDate: "11/11/2022",
    stolenContactNumber: null,
    financeRecordCount: 1,
    plateChangeCount: 1,
    scrapDate: null,
    exportDate: null,
    plateChangeList: [{ previousVrm: "NC19NCD", dateChanged: "21/11/2022" }],
    exported: false,
    highRiskRecordList: null,
    transmissionType: "Manual",
    scrapped: false,
    writtenOff: false,
    yearOfManufacture: "2019",
    engineCapacity: 1497,
    stolen: false,
    previousKeepers: null,
    imported: false,
    vicTested: null,
    colour: "SILVER",
    financeRecordList: [
        {
            agreementDate: "2022-11-09T00:00:00",
            agreementType: "PCP",
            agreementTerm: 49,
            agreementNumber: "00000000045022045601452",
            financeCompany: "HYUNDAI CAPITAL UK",
            vehicleDescription: "FORD FIESTA HATCHBACK 1.5"
        }
    ],
    insight: "The Ford Fiesta ST-2 Turbo was first registered on 31/07/2019 and has had one previous keeper, with the latest keeper change on 11/11/2022. It has a current mileage of 71,064 and has not been written off or reported stolen. The car has a finance agreement under PCP with Hyundai Capital UK. The real-world MPG for a Ford Fiesta ST-2 Turbo is around 35 MPG. At this rate, driving 10,000 miles a year would cost approximately £1,700 in fuel. This model is generally known for its reliability."
};

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${parseInt(parts[0])} ${months[parseInt(parts[1]) - 1]} ${parts[2]}`;
    }
    return dateStr;
};

export default function SampleCheck() {
    return (
        <div className='fc-page'>

            {/* Header */}
            <div className='fc-header'>
                <p className='fc-plate'>{data.vrm}</p>
                <h1 className='fc-title'>{data.make} {data.model}</h1>
                <div className='fc-details-bar'>
                    {data.dateFirstRegistered && <span><FaCalendarAlt /> {formatDate(data.dateFirstRegistered)}</span>}
                    {data.fuelType && <span><FaGasPump /> {data.fuelType}</span>}
                    {data.transmissionType && <span><FaCog /> {data.transmissionType}</span>}
                    {data.colour && <span>{data.colour}</span>}
                </div>

                {data.writtenOff && (
                    <div className='fc-alert danger'>
                        <FaExclamationTriangle />
                        This vehicle has been written off{data.writeOffCategory ? ` (Category ${data.writeOffCategory})` : ''}
                        {data.writeOffDate ? ` on ${formatDate(data.writeOffDate)}` : ''}
                    </div>
                )}
                {data.stolen && (
                    <div className='fc-alert danger'>
                        <FaExclamationTriangle />
                        This vehicle has been reported stolen
                    </div>
                )}
                {data.imported && (
                    <div className='fc-alert warning'>
                        <FaExclamationTriangle />
                        This vehicle was imported{data.importedFromOutsideEu ? ' from outside the EU' : ''}
                    </div>
                )}
            </div>

            {/* AI Overview */}
            {data.insight && (
                <div className='fc-section'>
                    <h3 className='fc-section-title'>AI Overview</h3>
                    <p className='fc-insight'>{data.insight}</p>
                </div>
            )}

            {/* Check Summary */}
            <div className='fc-section'>
                <h3 className='fc-section-title'>Check Summary</h3>
                <div className='fc-status-grid'>
                    <div className={`fc-status-item ${data.financeRecordCount === 0 ? 'clear' : 'bad'}`}>
                        <FaMoneyBillWave className='fc-status-icon' />
                        <span className='fc-status-label'>Finance</span>
                        <span className='fc-status-value'>{data.financeRecordCount === 0 ? 'NONE' : `${data.financeRecordCount} RECORD${data.financeRecordCount > 1 ? 'S' : ''}`}</span>
                    </div>
                    <div className={`fc-status-item ${!data.writtenOff ? 'clear' : 'bad'}`}>
                        <FaCarCrash className='fc-status-icon' />
                        <span className='fc-status-label'>Write-Off</span>
                        <span className='fc-status-value'>{data.writtenOff ? (data.writeOffCategory || 'YES') : 'NONE'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.stolen ? 'clear' : 'bad'}`}>
                        <FaShieldAlt className='fc-status-icon' />
                        <span className='fc-status-label'>Stolen</span>
                        <span className='fc-status-value'>{data.stolen ? 'YES' : 'NONE'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.scrapped ? 'clear' : 'bad'}`}>
                        <FaCarCrash className='fc-status-icon' />
                        <span className='fc-status-label'>Scrapped</span>
                        <span className='fc-status-value'>{data.scrapped ? 'YES' : 'NO'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.exported ? 'clear' : 'warn'}`}>
                        <FaGlobe className='fc-status-icon' />
                        <span className='fc-status-label'>Exported</span>
                        <span className='fc-status-value'>{data.exported ? 'YES' : 'NO'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.imported ? 'clear' : 'warn'}`}>
                        <FaGlobe className='fc-status-icon' />
                        <span className='fc-status-label'>Imported</span>
                        <span className='fc-status-value'>{data.imported ? 'YES' : 'NO'}</span>
                    </div>
                </div>
            </div>

            {/* Key Details */}
            <div className='fc-section'>
                <h3 className='fc-section-title'>Key Details</h3>
                <div className='fc-details-grid'>
                    {data.dateFirstRegistered && <div className='fc-detail-item'><span className='fc-detail-label'>First Registered</span><span className='fc-detail-value'>{formatDate(data.dateFirstRegistered)}</span></div>}
                    {data.latestV5cIssuedDate && <div className='fc-detail-item'><span className='fc-detail-label'>Last V5c Issued</span><span className='fc-detail-value'>{formatDate(data.latestV5cIssuedDate)}</span></div>}
                    {data.latestKeeperChangeDate && <div className='fc-detail-item'><span className='fc-detail-label'>Last Keeper Change</span><span className='fc-detail-value'>{formatDate(data.latestKeeperChangeDate)}</span></div>}
                    <div className='fc-detail-item'><span className='fc-detail-label'>Previous Keepers</span><span className='fc-detail-value'>{data.previousKeeperCount}</span></div>
                    {data.vinLast5 && <div className='fc-detail-item'><span className='fc-detail-label'>VIN Last 5</span><span className='fc-detail-value'>{data.vinLast5}</span></div>}
                    {data.yearOfManufacture && <div className='fc-detail-item'><span className='fc-detail-label'>Year of Manufacture</span><span className='fc-detail-value'>{data.yearOfManufacture}</span></div>}
                    {data.engineCapacity > 0 && <div className='fc-detail-item'><span className='fc-detail-label'>Engine</span><span className='fc-detail-value'>{data.engineCapacity}cc</span></div>}
                    {data.transmissionType && <div className='fc-detail-item'><span className='fc-detail-label'>Transmission</span><span className='fc-detail-value'>{data.transmissionType}</span></div>}
                    {data.colour && <div className='fc-detail-item'><span className='fc-detail-label'>Colour</span><span className='fc-detail-value'>{data.colour}</span></div>}
                    {data.fuelType && <div className='fc-detail-item'><span className='fc-detail-label'>Fuel Type</span><span className='fc-detail-value'>{data.fuelType}</span></div>}
                    {data.plateChangeCount > 0 && <div className='fc-detail-item'><span className='fc-detail-label'>Plate Changes</span><span className='fc-detail-value'>{data.plateChangeCount}</span></div>}
                </div>
            </div>

            {/* Mileage History */}
            {data.mileageRecordList && data.mileageRecordList.length > 0 && (() => {
                const chartData = [...data.mileageRecordList]
                    .sort((a, b) => {
                        const [ad, am, ay] = a.dateOfInformation.split('/');
                        const [bd, bm, by] = b.dateOfInformation.split('/');
                        return new Date(`${ay}-${am}-${ad}`) - new Date(`${by}-${bm}-${bd}`);
                    })
                    .filter((r, i, arr) => i === 0 || r.dateOfInformation !== arr[i - 1].dateOfInformation)
                    .map(r => {
                        const [d, m, y] = r.dateOfInformation.split('/');
                        return { date: `${m}/${y.slice(2)}`, mileage: r.mileage };
                    });
                return (
                    <div className='fc-section'>
                        <h3 className='fc-section-title'>Mileage History</h3>
                        {data.mileageAnomalyDetected && (
                            <div className='fc-alert warning' style={{ marginBottom: 10 }}>
                                <FaExclamationTriangle /> Mileage anomaly detected — readings may be inconsistent
                            </div>
                        )}
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#6b7280' }} width={36} />
                                <Tooltip formatter={v => v.toLocaleString() + ' mi'} labelStyle={{ color: '#111827', fontWeight: 600 }} />
                                <Line type="monotone" dataKey="mileage" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: '#16a34a' }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                );
            })()}

            {/* Finance Records */}
            {data.financeRecordList && data.financeRecordList.length > 0 && (
                <div className='fc-section'>
                    <h3 className='fc-section-title'>Finance Records</h3>
                    {data.financeRecordList.map((record, i) => record && (
                        <div key={i} className='fc-finance-record'>
                            {record.financeCompany && <div className='fc-detail-item'><span className='fc-detail-label'>Finance Company</span><span className='fc-detail-value'>{record.financeCompany}</span></div>}
                            {record.agreementType && <div className='fc-detail-item'><span className='fc-detail-label'>Agreement Type</span><span className='fc-detail-value'>{record.agreementType}</span></div>}
                            {record.agreementDate && <div className='fc-detail-item'><span className='fc-detail-label'>Agreement Date</span><span className='fc-detail-value'>{record.agreementDate.split("T")[0]}</span></div>}
                            {record.agreementTerm && <div className='fc-detail-item'><span className='fc-detail-label'>Term</span><span className='fc-detail-value'>{record.agreementTerm} months</span></div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Plate Changes */}
            {data.plateChangeList && data.plateChangeList.length > 0 && (
                <div className='fc-section'>
                    <h3 className='fc-section-title'>Plate Changes</h3>
                    <div className='fc-details-grid'>
                        {data.plateChangeList.map((change, i) => change && (
                            <div key={i} className='fc-detail-item'>
                                <span className='fc-detail-label'>Previous Plate</span>
                                <span className='fc-detail-value'>{change.previousVrm}{change.dateChanged ? ` — ${formatDate(change.dateChanged)}` : ''}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
}
